/*
 * Bracket accent font
 * ---------------------------------------------------------------------------
 * Theme-wide: wherever an author types [bracketed] text, the text inside the
 * brackets is wrapped in <span class="bracket-font"> and the brackets removed,
 * so e.g. "Red [Lenses]" renders as "Red Lenses" with "Lenses" in the accent
 * font. Runs on load and watches for dynamically added/re-rendered content.
 *
 * Safe by design: never touches form fields, scripts, styles, code, SVG, or
 * editable regions; disconnects its observer while mutating to avoid loops;
 * and the text it inserts contains no brackets, so it can never re-process
 * its own output.
 */
(function () {
  'use strict';

  var FONT_CLASS = 'bracket-font';
  // [text] on a single run, no nested/empty brackets, no line breaks.
  var BRACKET_RE = /\[([^\[\]\r\n]+)\]/g;

  var HTML_NS = 'http://www.w3.org/1999/xhtml';

  // Elements whose text must never be altered.
  var SKIP_TAGS = {
    SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, INPUT: 1, SELECT: 1,
    OPTION: 1, CODE: 1, PRE: 1, KBD: 1, SAMP: 1, VAR: 1,
    TEMPLATE: 1, SVG: 1, MATH: 1, TITLE: 1,
    // The theme's line-by-line heading animation renders from a shadow-DOM copy,
    // so editing its light DOM does nothing useful — leave it alone.
    'SPLIT-LINES': 1
  };

  function isSkippable(el) {
    while (el && el.nodeType === 1) {
      // Non-HTML namespaces (SVG, MathML) — never inject <span> there.
      if (el.namespaceURI && el.namespaceURI !== HTML_NS) return true;
      if (SKIP_TAGS[el.nodeName.toUpperCase()]) return true;
      if (el.isContentEditable) return true;
      if (el.classList && el.classList.contains(FONT_CLASS)) return true;
      el = el.parentNode;
    }
    return false;
  }

  function processTextNode(node) {
    var text = node.nodeValue;
    if (!text || text.indexOf('[') === -1) return;
    BRACKET_RE.lastIndex = 0;
    if (!BRACKET_RE.test(text)) return;
    if (isSkippable(node.parentNode)) return;

    BRACKET_RE.lastIndex = 0;
    var frag = document.createDocumentFragment();
    var lastIndex = 0;
    var match;
    while ((match = BRACKET_RE.exec(text)) !== null) {
      if (match.index > lastIndex) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }
      var span = document.createElement('span');
      span.className = FONT_CLASS;
      span.textContent = match[1];
      frag.appendChild(span);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    if (node.parentNode) node.parentNode.replaceChild(frag, node);
  }

  function process(root) {
    if (!root) return;
    if (root.nodeType === 3) { processTextNode(root); return; }
    if (root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11) return;
    if (root.nodeType === 1 && isSkippable(root)) return;

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    var n;
    while ((n = walker.nextNode())) {
      if (n.nodeValue && n.nodeValue.indexOf('[') !== -1) nodes.push(n);
    }
    for (var i = 0; i < nodes.length; i++) processTextNode(nodes[i]);
  }

  var observer = null;
  function startObserver() {
    if (!('MutationObserver' in window)) return;
    observer = new MutationObserver(function (mutations) {
      // Pause observation while we mutate so our own inserts don't retrigger us.
      observer.disconnect();
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType === 1) process(node);
          else if (node.nodeType === 3) processTextNode(node);
        }
      }
      observer.observe(document.body, { childList: true, subtree: true });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    process(document.body);

    // In the theme editor, a page-wide MutationObserver that rewrites DOM
    // fights Shopify's own section re-rendering: the editor replaces a
    // section's HTML, we mutate it, the editor reconciles it back, we mutate
    // again — a ping-pong loop that pegs the main thread and freezes the tab.
    // So in design mode we skip the global observer and instead re-process
    // only the section that just (re)loaded.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', function (event) {
        process(event.target);
      });
      return;
    }

    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
